import http from '@/services/http'
import { delayed } from '@/utils'

export default {
  namespaced: true,
  state: {
    fetchInProgress: false,
    programs: [],
    saveInProgress: false,
    activationInProgress: false,
    programIdBeingActivated: null,
    programBeingAdded: false,
    programIdBeingCloned: null,
  },
  mutations: {
    START_FETCH(state) {
      state.fetchInProgress = true
    },
    STOP_FETCH(state) {
      state.fetchInProgress = false
    },
    SET_PROGRAMS(state, programs) {
      state.programs = programs
    },

    START_SAVE(state) {
      state.saveInProgress = true
    },
    STOP_SAVE(state) {
      state.saveInProgress = false
    },

    START_ACTIVATE(state, { programId }) {
      state.programIdBeingActivated = programId
    },
    STOP_ACTIVATE(state, { programId }) {
      state.programIdBeingActivated = null
    },
    ACTIVATED(state, { programId }) {
      state.programs = state.programs.map((p) => {
        if (p.id === programId) {
          p.is_active = true
        }
        return { ...p }
      })
    },
    DEACTIVATED(state, { programId }) {
      state.programs = state.programs.map((p) => {
        if (p.id === programId) {
          p.is_active = false
        }
        return { ...p }
      })
    },

    START_ADDING(state) {
      state.programBeingAdded = true
    },
    STOP_ADDING(state) {
      state.programBeingAdded = false
    },

    START_CLONING(state, { programId }) {
      state.programIdBeingCloned = programId
    },
    STOP_CLONING(state) {
      state.programIdBeingCloned = null
    },

    remove(state, { programId }) {
      state.programs = state.programs.filter((p) => p.id !== programId)
    },
  },
  getters: {
    isProgramBeingActivated: (state) => (programId) =>
      state.programIdBeingActivated === programId,
    isProgramBeingCloned: (state) => (programId) =>
      state.programIdBeingCloned === programId,
  },
  actions: {
    async fetch({ commit }, { organizationId }) {
      commit('START_FETCH')
      const url = `/organizations/${organizationId}/programs`
      const response = await delayed(http.getAuthed(url), 500)
      commit('SET_PROGRAMS', response.data)
      commit('STOP_FETCH')
    },
    async save({ commit, state }, { name }) {
      commit('START_SAVE')
      const data = { name }
      await http.postAuthed(
        `/organizations/${state.organization.id}/update`,
        data,
      )
      commit('SET_ORGANIZATION_DATA', data)
      commit('STOP_SAVE')
    },
    async activate(
      { commit, state },
      { organizationId, programId, delay = 1000 },
    ) {
      return setIsActivate(
        { commit, state },
        { organizationId, programId, delay, isActive: true },
      )
    },
    async deactivate(
      { commit, state },
      { organizationId, programId, delay = 1000 },
    ) {
      return setIsActivate(
        { commit, state },
        { organizationId, programId, delay, isActive: false },
      )
    },
    async clone(
      { commit, state },
      { organizationId, programId, programName, delay = 1000 },
    ) {
      commit('START_CLONING', { programId })
      const url = `/organizations/${organizationId}/clone-program`
      try {
        const request = http.postAuthed(url, {
          program_id: programId,
          name: programName,
        })
        return await delayed(request, delay)
      } finally {
        commit('STOP_CLONING', { programId })
      }
    },
    async add(
      { commit, state },
      { organizationId, programName, delay = 1000 },
    ) {
      commit('START_ADDING')
      const url = `/organizations/${organizationId}/add-program`
      try {
        const request = http.postAuthed(url, {
          name: programName,
        })
        return await delayed(request, delay)
      } finally {
        commit('STOP_ADDING')
      }
    },
    async remove(
      { commit },
      { organizationId, programId, removeImmediately = false },
    ) {
      if (removeImmediately) {
        commit('remove', { programId })
      }
      const url = `/organizations/${organizationId}/remove-program`
      const request = http.postAuthed(url, { program_id: programId })
      const response = await request
      if (!removeImmediately) {
        commit('remove', { programId })
      }
      return response.data
    },
  },
}

async function setIsActivate(
  { commit, state },
  { organizationId, programId, delay = 1000, isActive },
) {
  commit('START_ACTIVATE', { programId })
  const mutation = isActive ? 'ACTIVATED' : 'DEACTIVATED'
  const command = isActive ? 'activate' : 'deactivate'
  const url = `/organizations/${organizationId}/${command}-program`
  try {
    const request = http.postAuthed(url, { program_id: programId })
    const response = await delayed(request, delay)
    commit(mutation, { programId })
    return response
  } finally {
    commit('STOP_ACTIVATE', { programId })
  }
}
